import { DateTime } from 'luxon';
import { t } from '../services/texts.service';

/**
 * Helper class for working with DateTime objects.
 */
export class DateTimeHelper {
  /**
   * Parses a date-time string into a Luxon DateTime object.
   *
   * Requires an ISO 8601 format string with timezone (e.g., "2025-12-11T12:34:56+01:00").
   *
   * @param inputString - The date-time string to parse in ISO 8601 format with timezone
   * @returns A Luxon DateTime object representing the parsed date and time in 'Europe/Stockholm' timezone
   * @throws {Error} If the input string is empty, undefined, or cannot be parsed as ISO 8601 with timezone
   *
   * @example
   * ```typescript
   * // ISO 8601 format with timezone
   * const dt1 = DateTimeHelper.parse("2023-12-31T23:59:59+01:00");
   * const dt2 = DateTimeHelper.parse("2025-12-11T12:34:56Z");
   * ```
   */
  public static parse(inputString: string): DateTime {
    if (!inputString) {
      throw new Error('Input string is empty or undefined');
    }

    // Check if the input string contains a timezone indicator (Z or +/- offset)
    const hasTimezone = /[+-]\d{2}:\d{2}$|Z$/.test(inputString);
    if (!hasTimezone) {
      throw new Error(`Missing timezone information in date string: ${inputString}. Expected ISO 8601 format with timezone (e.g., "2025-12-11T12:34:56+01:00")`);
    }

    // Parse ISO 8601 format with timezone
    const iso8601DateTime = DateTime.fromISO(inputString, { setZone: true });

    if (!iso8601DateTime.isValid) {
      throw new Error(`Unable to parse date string: ${inputString}. Expected ISO 8601 format with timezone (e.g., "2025-12-11T12:34:56+01:00")`);
    }

    return iso8601DateTime.setZone('Europe/Stockholm');
  }
  /**
   * Calculates the difference in minutes between two DateTime objects.
   *
   * @param from - The starting DateTime
   * @param to - The ending DateTime
   * @returns The difference in minutes, rounded to the nearest integer. Returns 0 if the difference cannot be calculated.
   */
  public static minutes(from: DateTime | undefined, to: DateTime | undefined): number {
    if (!from || !to) return 0;
    const durationInMinutes = to.diff(from, 'minutes');
    if (!durationInMinutes.isValid) return 0;
    return Math.round(durationInMinutes.minutes);
  }

  /**
   * Checks if two DateTime objects represent the same calendar day.
   *
   * @param date1 - The first DateTime object to compare, or undefined
   * @param date2 - The second DateTime object to compare, or undefined
   * @returns `true` if both dates are defined and represent the same day, `false` otherwise
   *
   * @example
   * ```typescript
   * const date1 = DateTime.fromISO('2023-01-15T10:30:00');
   * const date2 = DateTime.fromISO('2023-01-15T18:45:00');
   * DateTimeHelper.isSameDay(date1, date2); // returns true
   * ```
   */
  public static isSameDay(date1: DateTime | undefined, date2: DateTime | undefined): boolean {
    if (!date1 || !date2) {
      return false;
    }
    return date1.hasSame(date2, 'day');
  }

  /**
   * Converts the duration between two DateTime objects into a human-readable string format.
   *
   * @param from - The starting DateTime
   * @param to - The ending DateTime
   * @returns A formatted duration string in the format "X h Y min" or "Y min".
   *          Returns an empty string if either parameter is undefined.
   *          Negative durations are prefixed with a minus sign.
   *
   * @example
   * ```typescript
   * const start = DateTime.fromISO('2024-01-01T10:00:00');
   * const end = DateTime.fromISO('2024-01-01T11:30:00');
   * const duration = DateTimeHelper.toDuration(start, end);
   * // Returns: "1 h 30 min"
   * ```
   */
  public static toDuration(from: DateTime, to: DateTime): string {
    if (from === undefined || to === undefined) {
      return '';
    }

    let minutes = DateTimeHelper.minutes(from, to);
    const negativeValue = minutes < 0;
    if (negativeValue) {
      minutes = Math.abs(minutes);
    }

    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0) {
      return negativeValue ? `-${h} h ${m} min` : `${h} h ${m} min`;
    }
    return negativeValue ? `-${m} min` : `${m} min`;
  }

  /**
   * Converts a DateTime object to a long date string format.
   *
   * @param dateTime - The DateTime object to format, or undefined
   * @param showDayOfWeek - Whether to include the day of the week in the output. When true, the day name
   *                        is capitalized and prefixed to the date. Special handling for "Idag" (Today),
   *                        "Igår" (Yesterday), and "Imorgon" (Tomorrow). Defaults to false.
   * @returns A formatted date string in the format "d MMMM yyyy" (e.g., "15 januari 2024") when showDayOfWeek
   *          is false, or "EEEE d MMMM yyyy" (e.g., "Tisdag 15 januari 2024") when showDayOfWeek is true.
   *          Returns an empty string if dateTime is undefined.
   *
   * @example
   * ```typescript
   * // Without day of week
   * const date = DateTime.fromISO('2024-03-15');
   * const formatted = toLongDate(date); // Returns "15 mars 2024"
   *
   * // With day of week
   * const dateWithDay = DateTime.fromISO('2025-09-02'); // Tuesday
   * const formattedWithDay = toLongDate(dateWithDay, true); // Returns "Tisdag 2 september 2025"
   *
   * // Today's date with day of week
   * const today = DateTime.now();
   * const formattedToday = toLongDate(today, true); // Returns "Idag 2 december 2025"
   *
   * const empty = toLongDate(undefined); // Returns ""
   * ```
   */
  public static toLongDate(dateTime: DateTime | undefined, showDayOfWeek: boolean = false): string {
    if (!dateTime) return '';

    const longDateFormatString = 'd MMMM yyyy';

    const longDate = dateTime.toFormat(longDateFormatString);
    if (!showDayOfWeek) return longDate;

    // Handle showing weekday names for today, yesterday, and tomorrow
    const today = DateTime.now().startOf('day');
    const yesterday = today.minus({ days: 1 });
    const tomorrow = today.plus({ days: 1 });
    let dayOfWeek: string;
    if (DateTimeHelper.isSameDay(dateTime, today)) {
      dayOfWeek = t('Idag', 'Today');
    } else if (DateTimeHelper.isSameDay(dateTime, yesterday)) {
      dayOfWeek = t('Igår', 'Yesterday');
    } else if (DateTimeHelper.isSameDay(dateTime, tomorrow)) {
      dayOfWeek = t('Imorgon', 'Tomorrow');
    } else {
      dayOfWeek = dateTime.toFormat('EEEE');
    }
    dayOfWeek = dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);
    return `${dayOfWeek} ${longDate}`;
  }

  /**
   * Converts a DateTime object to a time string in HH:mm format.
   *
   * @param dateTime - The DateTime object to convert, or undefined
   * @returns A time string in HH:mm format (e.g., "14:30"), or an empty string if dateTime is undefined
   *
   * @example
   * ```typescript
   * const dt = DateTime.fromISO('2023-10-15T14:30:00');
   * const timeString = toTime(dt); // Returns "14:30"
   *
   * const emptyTime = toTime(undefined); // Returns ""
   * ```
   */
  public static toTime(dateTime: DateTime | undefined): string {
    return dateTime ? dateTime.toFormat('HH:mm') : '';
  }
}
